/**
 * ClawbackReasonStatCards - Grid of stat cards showing clawbacks by reason
 *
 * Displays:
 * - Total clawbacks card
 * - Breakdown by each reason category
 * - Click to filter by reason
 *
 * All cards have consistent height and use flexbox to avoid empty spaces
 */

import React from 'react';
import { formatCurrency } from '../../utils/formatters';
import {
  ClawbackReason,
  ClawbackMetricsByReason,
  CLAWBACK_REASON_LABELS,
  CLAWBACK_REASON_SHORT_LABELS,
  CLAWBACK_REASON_COLORS,
  CLAWBACK_REASON_DISPLAY_ORDER,
} from '../../types/clawbackReasons';
import {
  AlertTriangle,
  Building,
  Home,
  TrendingDown,
  HandCoins,
  DollarSign,
  Heart,
  Users,
  Frown,
  Target,
  MapPin,
  HelpCircle,
  Clock,
} from 'lucide-react';

interface ClawbackReasonStatCardsProps {
  metrics: ClawbackMetricsByReason | null;
  selectedReason: ClawbackReason | 'all' | 'needs_categorization';
  onReasonSelect: (reason: ClawbackReason | 'all' | 'needs_categorization') => void;
  isLoading?: boolean;
}

// Icons for each reason
const REASON_ICONS: Record<ClawbackReason, React.ReactNode> = {
  bridging_loan: <Building className="w-4 h-4" />,
  lost_to_competitor: <TrendingDown className="w-4 h-4" />,
  property_sold: <Home className="w-4 h-4" />,
  paid_off: <HandCoins className="w-4 h-4" />,
  better_rate: <DollarSign className="w-4 h-4" />,
  financial_hardship: <Frown className="w-4 h-4" />,
  relationship_settlement: <Users className="w-4 h-4" />,
  investment_sold: <Home className="w-4 h-4" />,
  lender_dissatisfaction: <Target className="w-4 h-4" />,
  product_mismatch: <Target className="w-4 h-4" />,
  customer_moved: <MapPin className="w-4 h-4" />,
  deceased: <Heart className="w-4 h-4" />,
  unknown: <HelpCircle className="w-4 h-4" />,
};

// Consistent card styling for all stat cards
const CARD_BASE_CLASSES = 'bg-gray-800 rounded-lg p-3 border text-left transition-all hover:bg-gray-750';

const ClawbackReasonStatCards: React.FC<ClawbackReasonStatCardsProps> = ({
  metrics,
  selectedReason,
  onReasonSelect,
  isLoading = false,
}) => {
  if (isLoading || !metrics) {
    return (
      <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(8.75rem, 1fr))' }}>
        {/* Skeleton cards */}
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className={`${CARD_BASE_CLASSES} border-gray-700 animate-pulse`}>
            <div className="h-4 bg-gray-700 rounded w-16 mb-2" />
            <div className="h-6 bg-gray-700 rounded w-12 mb-1" />
            <div className="h-3 bg-gray-700 rounded w-20" />
          </div>
        ))}
      </div>
    );
  }

  // Only show reason cards that have actual clawbacks (count > 0)
  // This prevents orphan cards and keeps the UI clean
  const reasonsWithCounts = CLAWBACK_REASON_DISPLAY_ORDER.filter(
    (reason) => {
      const count = metrics.byReason[reason];
      return count !== undefined && count !== null && count > 0;
    }
  );

  return (
    <div className="space-y-3">
      {/* CSS Grid with auto-fill - cards have consistent height across all rows */}
      <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(8.75rem, 1fr))' }}>
        {/* Total Clawbacks Card */}
        <button
          onClick={() => onReasonSelect('all')}
          className={`${CARD_BASE_CLASSES} ${
            selectedReason === 'all'
              ? 'border-red-500 ring-1 ring-red-500/50'
              : 'border-gray-700 hover:border-gray-600'
          }`}
        >
          <div className="flex items-center gap-1.5 text-xs mb-1.5">
            <AlertTriangle className="w-4 h-4 text-red-400" />
            <span className="text-gray-400">Total</span>
          </div>
          <div className="flex items-baseline gap-2">
            <p className="text-xl font-bold text-white">{metrics.total.toLocaleString()}</p>
          </div>
          <p className="text-xs text-gray-500 mt-0.5 truncate">
            {formatCurrency(metrics.totalAmountCents)}
          </p>
        </button>

        {/* Needs Categorization Card - only show if count > 0 */}
        {metrics.needsCategorization > 0 && (
          <button
            onClick={() => onReasonSelect('needs_categorization')}
            className={`${CARD_BASE_CLASSES} ${
              selectedReason === 'needs_categorization'
                ? 'border-amber-500 ring-1 ring-amber-500/50'
                : 'border-gray-700 hover:border-gray-600'
            }`}
          >
            <div className="flex items-center gap-1.5 text-xs mb-1.5">
              <Clock className="w-4 h-4 text-amber-400" />
              <span className="text-gray-400 truncate">Uncategorised</span>
            </div>
            <div className="flex items-baseline gap-2">
              <p className="text-xl font-bold text-amber-400">
                {metrics.needsCategorization.toLocaleString()}
              </p>
              {metrics.total > 0 && (
                <span className="text-xs text-gray-500">
                  {((metrics.needsCategorization / metrics.total) * 100).toFixed(0)}%
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500 mt-0.5 truncate">
              {formatCurrency(metrics.needsCategorizationAmountCents)}
            </p>
          </button>
        )}

        {/* Reason Cards - only those with count > 0 */}
        {reasonsWithCounts.map((reason) => {
          const count = metrics.byReason[reason] || 0;
          const value = metrics.byReasonAmountCents[reason] || 0;
          const colors = CLAWBACK_REASON_COLORS[reason];
          const isSelected = selectedReason === reason;
          const percentage = metrics.total > 0 ? ((count / metrics.total) * 100).toFixed(0) : '0';

          return (
            <button
              key={reason}
              onClick={() => onReasonSelect(reason)}
              className={`${CARD_BASE_CLASSES} ${
                isSelected
                  ? `${colors.border} ring-1 ring-opacity-50`
                  : 'border-gray-700 hover:border-gray-600'
              }`}
            >
              <div className="flex items-center gap-1.5 text-xs mb-1.5">
                <span className={colors.text}>{REASON_ICONS[reason]}</span>
                <span className="text-gray-400 truncate">{CLAWBACK_REASON_SHORT_LABELS[reason]}</span>
              </div>
              <div className="flex items-baseline gap-2">
                <p className={`text-xl font-bold ${colors.text}`}>
                  {count.toLocaleString()}
                </p>
                <span className="text-xs text-gray-500">{percentage}%</span>
              </div>
              <p className="text-xs text-gray-500 mt-0.5 truncate">
                {formatCurrency(value)}
              </p>
            </button>
          );
        })}
      </div>

      {/* Selected filter indicator */}
      {selectedReason !== 'all' && (
        <div className="flex items-center gap-2 text-sm">
          <span className="text-gray-400">Filtering by:</span>
          {selectedReason === 'needs_categorization' ? (
            <span className="px-2 py-0.5 rounded bg-amber-900/30 text-amber-400 border-amber-500/30 border">
              Needs Categorization
            </span>
          ) : (
            <span className={`px-2 py-0.5 rounded ${CLAWBACK_REASON_COLORS[selectedReason].bg} ${CLAWBACK_REASON_COLORS[selectedReason].text} ${CLAWBACK_REASON_COLORS[selectedReason].border} border`}>
              {CLAWBACK_REASON_LABELS[selectedReason]}
            </span>
          )}
          <button
            onClick={() => onReasonSelect('all')}
            className="text-gray-500 hover:text-white transition-colors"
          >
            Clear
          </button>
        </div>
      )}
    </div>
  );
};

export default ClawbackReasonStatCards;
